import React from 'react';
import type { KeyboardEvent, ReactElement, ReactNode, SyntheticEvent } from 'react';

import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import type { StackProps } from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system/styleFunctionSx';

import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import GuideButton from 'components/GuideButton';
import { useAppSelector } from 'hooks/redux';

export type StdDialogTitleProps = Omit<StackProps, 'sx'> & {
  children?: ReactNode;
  /** Disables the close button while keeping it visible. */
  closeButtonDisabled?: boolean;
  /** Header background. Defaults to the paper color. */
  colorBar?: string;
  /** Merged into the header row's styles. */
  contentSx?: SystemStyleObject<Theme>;
  guideId?: string;
  headerActions?: ReactNode;
  onBack?: (event?: SyntheticEvent) => void;
  onClose?: (event: SyntheticEvent, reason: 'CLOSE_BUTTON') => void;
  showBackButton?: boolean;
  showCloseButton?: boolean;
  showDivider?: boolean;
  /** Adds top padding. */
  spacious?: boolean;
  /** Merged into the title text's styles. */
  sx?: SystemStyleObject<Theme>;
  /** ID applied to the title content for the dialog's accessible name. */
  titleId?: string;
  /** Only dialogs can be dragged. */
  variant?: 'dialog' | 'drawer';
};

/**
 * StdDialogTitle should be used instead of the MUI DialogTitle. The primary purpose of this component is to support an
 * optional back and close button. Due to the way MUI DialogTitle was written, this does not wrap that component.
 *
 * This component is meant to be used with StdDialog.
 */
const StdDialogTitle = (props: StdDialogTitleProps): ReactElement => {
  const theme = useTheme();

  const {
    children,
    closeButtonDisabled = false,
    colorBar = theme.palette.background.paper,
    guideId,
    headerActions,
    id,
    onBack,
    onClose,
    sharedcomponentid,
    showBackButton,
    showCloseButton,
    sx: ferrySx = {},
    showDivider = true,
    titleId,
    variant = 'dialog',
    spacious = false,
    contentSx,
    ...other
  } = props;

  const handleBackKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter') onBack?.();
  };

  const handleCloseButton = (event: SyntheticEvent) => {
    onClose?.(event, 'CLOSE_BUTTON');
  };

  function renderBackButton() {
    if (showBackButton) {
      return (
        <IconButton
          size='small'
          variant='tonal'
          aria-label='Back'
          id='dlg-back'
          onClick={onBack}
//////////////////////////////////////////////////////
          onTouchEnd={onBack}
///////////////////
          onKeyDown={handleBackKeyDown}
        >
          <ArrowBackRounded />
        </IconButton>
      );
    }
    return null;
  }

  function renderCloseButton() {
    if (showCloseButton) {
      return (
        <IconButton
          disabled={closeButtonDisabled}
          size='small'
          variant='tonal'
          aria-label='Close'
          id='dlg-close'
          onClick={handleCloseButton}
//////////////////////////////////////////////////////
          onTouchEnd={handleCloseButton}
///////////////////
        >
          <CloseRounded />
        </IconButton>
      );
    }
    return null;
  }

  const dragDialogFlag = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'dragDialog'));
  const dragDialog = Boolean(dragDialogFlag) && variant === 'dialog';

  return (
    <Stack>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        id={id || 'std-dialog-title'}
        sharedcomponentid={sharedcomponentid || 'STD_DIALOG'}
        sx={{
          backgroundColor: colorBar,
          px: 3,
          pt: spacious ? 3 : 2,
          pb: 2,
          ...contentSx,
        }}
        {...other}
      >
        <Stack
          className='draggable-dialog-title'
          id={titleId ?? 'draggable-dialog-title'}
          direction='row'
          gap={2}
          alignItems='center'
          sx={{ ...(dragDialog && { cursor: 'move' }) }}
        >
          {dragDialog && <DragIndicatorIcon color='disabled' fontSize='medium' />}
          {renderBackButton()}
          <Typography
            align='left'
            component='h2'
            variant='titleMedium'
            sx={{ width: '100%', lineHeight: '40px', ...ferrySx }}
          >
            {children}
          </Typography>
        </Stack>
        <Stack direction='row'>
          {headerActions}
          <GuideButton guideId={guideId} />
          {renderCloseButton()}
        </Stack>
      </Stack>
      {showDivider && <Divider />}
    </Stack>
  );
};

export default StdDialogTitle;
